import { AlertStatus } from '@make-software/cspr-ui';
import { Alert, BodyText } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import React from 'react';
import { getExpirationPeriod } from '../../redux/utils';

export const INITIAL_STATE = 'initial';
export const SUBMITTED_STATE = 'submitted';
export const INFO_STATE = 'info';
export const PENDING_STATE = 'pending';
export const REJECTED_STATE = 'rejected';
export const EXPIRED_STATE = 'expired';
export const ALREADY_APPROVED_STATE = 'approved';

export type SigningState =
  | 'initial'
  | 'submitted'
  | 'pending'
  | 'rejected'
  | 'expired'
  | 'info'
  | 'approved';

interface TransactionStatusAlertProps {
  signingState: SigningState;
  error;
}

const StatusAccordingToState = {
  [SUBMITTED_STATE]: AlertStatus.Success,
  [REJECTED_STATE]: AlertStatus.Error,
  [PENDING_STATE]: AlertStatus.Pending,
  [INFO_STATE]: AlertStatus.Info,
  [EXPIRED_STATE]: AlertStatus.Error,
  [ALREADY_APPROVED_STATE]: AlertStatus.Info,
};

export const TransactionStatusAlert = ({
  signingState,
  error,
}: TransactionStatusAlertProps) => {
  const { t } = useTranslation();

  const alertTitle = () => {
    if (signingState === REJECTED_STATE) {
      return <Trans t={t}>Transaction was rejected.</Trans>;
    } else if (
      signingState === SUBMITTED_STATE ||
      signingState === PENDING_STATE
    ) {
      return <Trans t={t}>Transaction submitted.</Trans>;
    } else if (signingState === EXPIRED_STATE) {
      return <Trans t={t}>You can’t sign this transaction.</Trans>;
    } else if (signingState === ALREADY_APPROVED_STATE) {
      return <Trans t={t}>You've already signed this transaction.</Trans>;
    }

    return <Trans t={t}>Transaction error.</Trans>;
  };

  const alertMessage = () => {
    if (signingState === SUBMITTED_STATE || signingState === PENDING_STATE) {
      return (
        <BodyText size={3} variation={'black'}>
          <Trans t={t}>Awaiting your approval</Trans>
        </BodyText>
      );
    }
    if (signingState === EXPIRED_STATE) {
      const ttl = getExpirationPeriod();
      return (
        <BodyText size={3} variation={'black'}>
          <Trans t={t}>
            The transaction you are attempting to sign has already expired.
            Please create a new one and make sure to collect the needed
            signatures within {{ ttl }} hours.
          </Trans>
        </BodyText>
      );
    }
    if (signingState === ALREADY_APPROVED_STATE) {
      return (
        <BodyText size={3} variation={'black'}>
          {t(
            'The transaction you are attempting to sign already contains your signature.'
          )}
        </BodyText>
      );
    }
    if (error) {
      return (
        <BodyText size={3} variation={'black'}>
          {error}
        </BodyText>
      );
    }

    return (
      <>
        <Trans t={t}>
          <BodyText size={3} variation={'black'}>
            {' '}
            Check if your wallet is connected with the site and resend the sign
            prompt using the button below.
          </BodyText>
        </Trans>
      </>
    );
  };

  return (
    <>
      {signingState !== INITIAL_STATE && (
        <Alert
          status={StatusAccordingToState[signingState]}
          title={alertTitle()}
          message={alertMessage()}
        />
      )}
    </>
  );
};
